import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { documentService } from '../services/documentService'
import type { Document, DocumentFilters, DocumentStats, DocumentPlanLimits, DocumentType } from '../types/document'
import { 
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CameraIcon,
  MapPinIcon,
  BriefcaseIcon,
  DocumentDuplicateIcon,
  TagIcon,
  CalendarIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import AppHeader from '../components/AppHeader'

const DocumentsPage = () => {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const [documents, setDocuments] = useState<Document[]>([])
  const [stats, setStats] = useState<DocumentStats | null>(null)
  const [planLimits, setPlanLimits] = useState<DocumentPlanLimits | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<DocumentFilters>({})
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; documentId: string; documentTitle: string }>({
    show: false,
    documentId: '',
    documentTitle: ''
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (user) {
      loadDocuments()
      loadStats()
      loadPlanLimits()
    }
  }, [user])

  useEffect(() => {
    loadDocuments()
  }, [filters])

  // Reload data when user returns to the page
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        loadDocuments()
        loadStats()
        loadPlanLimits()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user])

  const loadDocuments = async () => {
    if (!user) return
    
    setIsLoading(true)
    const result = await documentService.getDocuments(user.id, filters)
    
    if (result.success && result.documents) {
      setDocuments(result.documents)
    } else {
      console.error('Failed to load documents:', result.error)
    }
    setIsLoading(false)
  }

  const loadStats = async () => {
    if (!user) return
    
    const result = await documentService.getDocumentStats(user.id)
    if (result.success && result.stats) {
      setStats(result.stats)
    }
  }

  const loadPlanLimits = async () => {
    if (!user) return
    
    const limits = await documentService.getPlanLimits(user.subscriptionStatus || 'freemium', user.id)
    setPlanLimits(limits)
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    setFilters({ ...filters, search: term })
  }

  const handleFilterChange = (newFilters: Partial<DocumentFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)
  }

  const handleDeleteDocument = (documentId: string, documentTitle: string) => {
    setDeleteConfirm({
      show: true,
      documentId,
      documentTitle
    })
  }

  const confirmDelete = async () => {
    if (!user || !deleteConfirm.documentId) return

    setIsDeleting(true)
    const result = await documentService.deleteDocument(deleteConfirm.documentId, user.id)

    if (result.success) {
      showSuccess('Success', 'Document deleted successfully')
      // Refresh the documents list
      loadDocuments()
      loadStats()
    } else {
      showError('Error', result.error || 'Failed to delete document')
    }

    setIsDeleting(false)
    setDeleteConfirm({ show: false, documentId: '', documentTitle: '' })
  }

  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case 'subcontractor_agreement':
      case 'independent_contractor':
        return <BriefcaseIcon className="w-5 h-5" />
      case 'nda':
        return <ShieldCheckIcon className="w-5 h-5" />
      case 'client_contract':
      case 'service_agreement':
        return <DocumentTextIcon className="w-5 h-5" />
      case 'model_release':
        return <CameraIcon className="w-5 h-5" />
      case 'location_release':
        return <MapPinIcon className="w-5 h-5" />
      case 'vendor_agreement':
        return <BuildingOfficeIcon className="w-5 h-5" />
      case 'employment_contract':
        return <UserGroupIcon className="w-5 h-5" />
      default:
        return <DocumentDuplicateIcon className="w-5 h-5" />
    }
  }

  const getStatusIcon = (status: Document['status']) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="w-4 h-4 text-gray-400" />
      case 'pending_review':
        return <ClockIcon className="w-4 h-4 text-yellow-400" />
      case 'active':
        return <CheckCircleIcon className="w-4 h-4 text-green-400" />
      case 'expired':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-400" />
      case 'terminated':
        return <XCircleIcon className="w-4 h-4 text-red-400" />
      case 'archived':
        return <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: Document['status']) => {
    switch (status) {
      case 'draft':
        return 'text-gray-400 bg-gray-500/20'
      case 'pending_review':
        return 'text-yellow-400 bg-yellow-500/20'
      case 'active':
        return 'text-green-400 bg-green-500/20'
      case 'expired':
        return 'text-red-400 bg-red-500/20'
      case 'terminated':
        return 'text-red-400 bg-red-500/20'
      case 'archived':
        return 'text-gray-400 bg-gray-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDocumentType = (type: DocumentType) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  // Apply search filter to documents
  const filteredDocuments = documents.filter(document => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    return (
      document.title.toLowerCase().includes(searchLower) ||
      document.description?.toLowerCase().includes(searchLower) ||
      document.category.toLowerCase().includes(searchLower) ||
      document.parties.some(party => party.name.toLowerCase().includes(searchLower))
    )
  })

  const handleDownloadPDF = (document: Document) => {
    const htmlContent = documentService.generatePDFContent(document)
    
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.print()
      }
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Documents & Contracts</h1>
              <p className="text-gray-400">Manage your business documents, contracts, and legal agreements</p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Link
                to="/documents/new"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-2 transition-all"
              >
                <PlusIcon className="w-5 h-5" />
                <span>New Document</span>
              </Link>
            </div>
          </div>

          {/* Plan Limits Bubble */}
          {planLimits && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-3 rounded-xl border ${
                planLimits.hasUnlimitedDocuments 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                    ? 'bg-orange-500/10 border-orange-500/30'
                    : 'bg-blue-500/10 border-blue-500/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    planLimits.hasUnlimitedDocuments 
                      ? 'bg-emerald-500/20' 
                      : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                        ? 'bg-orange-500/20'
                        : 'bg-blue-500/20'
                  }`}>
                    <DocumentTextIcon className={`w-4 h-4 ${
                      planLimits.hasUnlimitedDocuments 
                        ? 'text-emerald-400' 
                        : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                          ? 'text-orange-400'
                          : 'text-blue-400'
                    }`} />
                  </div>
                  <div>
                    <p className={`font-medium ${
                      planLimits.hasUnlimitedDocuments 
                        ? 'text-emerald-300' 
                        : planLimits.currentMonthUsage >= planLimits.maxDocumentsPerMonth * 0.8
                          ? 'text-orange-300'
                          : 'text-blue-300'
                    }`}>
                      {planLimits.hasUnlimitedDocuments 
                        ? 'Professional Plan - Unlimited Documents' 
                        : `${planLimits.currentMonthUsage}/${planLimits.maxDocumentsPerMonth} documents this month`
                      }
                    </p>
                    <p className="text-gray-400 text-sm">
                      {planLimits.hasUnlimitedDocuments 
                        ? 'Create unlimited business documents and contracts'
                        : `${planLimits.maxDocumentsPerMonth - planLimits.currentMonthUsage} documents remaining`
                      }
                    </p>
                  </div>
                </div>
                {!planLimits.hasUnlimitedDocuments && (
                  <Link
                    to="/upgrade"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Upgrade
                  </Link>
                )}
              </div>
            </motion.div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-mockup rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <DocumentTextIcon className="w-8 h-8 text-blue-400" />
                  <span className="text-2xl font-bold text-white">{stats.totalDocuments}</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Total Documents</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-mockup rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <CheckCircleIcon className="w-8 h-8 text-green-400" />
                  <span className="text-2xl font-bold text-white">{stats.activeContracts}</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Active Contracts</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-mockup rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <ClockIcon className="w-8 h-8 text-yellow-400" />
                  <span className="text-2xl font-bold text-white">{stats.pendingReview}</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Pending Review</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-mockup rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
                  <span className="text-2xl font-bold text-white">{stats.expiringThisMonth}</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Expiring Soon</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass-mockup rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <UserGroupIcon className="w-8 h-8 text-purple-400" />
                  <span className="text-2xl font-bold text-white">{stats.signaturesPending}</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Awaiting Signatures</p>
              </motion.div>
            </div>
          )}
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-xl p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search documents, parties, or categories..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FunnelIcon className="w-5 h-5" />
              <span>Filters</span>
            </button>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-gray-600"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Document Type</label>
                    <select
                      value={filters.documentType?.[0] || ''}
                      onChange={(e) => handleFilterChange({ 
                        documentType: e.target.value ? [e.target.value as DocumentType] : undefined 
                      })}
                      className="w-full px-3 py-2 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Types</option>
                      <option value="subcontractor_agreement">Subcontractor Agreement</option>
                      <option value="nda">NDA</option>
                      <option value="client_contract">Client Contract</option>
                      <option value="model_release">Model Release</option>
                      <option value="location_release">Location Release</option>
                      <option value="vendor_agreement">Vendor Agreement</option>
                      <option value="employment_contract">Employment Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                    <select
                      value={filters.status?.[0] || ''}
                      onChange={(e) => handleFilterChange({ 
                        status: e.target.value ? [e.target.value as Document['status']] : undefined 
                      })}
                      className="w-full px-3 py-2 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="pending_review">Pending Review</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="terminated">Terminated</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                    <select
                      value={filters.category || ''}
                      onChange={(e) => handleFilterChange({ category: e.target.value || undefined })}
                      className="w-full px-3 py-2 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Categories</option>
                      <option value="Contracts">Contracts</option>
                      <option value="Legal">Legal</option>
                      <option value="Releases">Releases</option>
                      <option value="Employment">Employment</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Documents List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-mockup rounded-xl overflow-hidden"
        >
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="p-8 text-center">
              <DocumentTextIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Documents Found</h3>
              <p className="text-gray-400 mb-6">
                {searchTerm ? 'No documents match your search criteria.' : 'Start by creating your first business document.'}
              </p>
              <Link
                to="/documents/new"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium inline-flex items-center space-x-2 transition-all"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Create Document</span>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-600">
              {filteredDocuments.map((document, index) => (
                <motion.div
                  key={document.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="p-2 bg-white/10 rounded-lg">
                          {getDocumentTypeIcon(document.documentType)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white truncate">
                            {document.title}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {formatDocumentType(document.documentType)} • {document.category}
                          </p>
                        </div>
                      </div>
                      
                      {document.description && (
                        <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                          {document.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="w-4 h-4" />
                          <span>Created {formatDate(document.createdAt)}</span>
                        </div>
                        {document.effectiveDate && (
                          <div className="flex items-center space-x-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Effective {formatDate(document.effectiveDate)}</span>
                          </div>
                        )}
                        {document.expirationDate && (
                          <div className="flex items-center space-x-1">
                            <ExclamationTriangleIcon className="w-4 h-4" />
                            <span>Expires {formatDate(document.expirationDate)}</span>
                          </div>
                        )}
                        {document.parties.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <UserGroupIcon className="w-4 h-4" />
                            <span>{document.parties.length} parties</span>
                          </div>
                        )}
                      </div>

                      {document.tags.length > 0 && (
                        <div className="flex items-center space-x-2 mt-3">
                          <TagIcon className="w-4 h-4 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                            {document.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ml-6 flex flex-col items-end space-y-3">
                      <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(document.status)}`}>
                        {getStatusIcon(document.status)}
                        <span className="capitalize">{document.status.replace('_', ' ')}</span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/documents/${document.id}`}
                          className="p-2 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 rounded-lg transition-colors"
                          title="View Document"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/documents/${document.id}/edit`}
                          className="p-2 bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 rounded-lg transition-colors"
                          title="Edit Document"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteDocument(document.id, document.title)}
                          className="p-2 bg-red-500/20 text-red-300 hover:bg-red-500/30 rounded-lg transition-colors"
                          title="Delete Document"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(document)}
                          className="p-2 bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 rounded-lg transition-colors"
                          title="Download PDF"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Plan Limits Info */}
        {planLimits && !planLimits.hasUnlimitedDocuments && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-300 font-medium">
                  Document Limit: {planLimits.currentMonthUsage}/{planLimits.maxDocumentsPerMonth} this month
                </p>
                <p className="text-yellow-200 text-sm">
                  Upgrade to Pro for unlimited documents and advanced features
                </p>
              </div>
              <Link
                to="/upgrade"
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Upgrade
              </Link>
            </div>
          </motion.div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-mockup rounded-xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold text-white mb-4">Delete Document</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete "{deleteConfirm.documentTitle}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, documentId: '', documentTitle: '' })}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-600/50 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default DocumentsPage 